import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingCart, MapPin, Clock, Phone, Search, Plus, Minus, X, Check, ChevronRight, Truck, Store, Sparkles, ChevronLeft, User, LogOut, Eye, EyeOff, Package, ClipboardList, Receipt, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";

export default function WebOrderPage() {
  const { slug } = useParams();
  const [accountId, setAccountId] = useState("");
  const [slugError, setSlugError] = useState(null);
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderType, setOrderType] = useState("pickup"); // pickup, delivery
  const [selectedCat, setSelectedCat] = useState("all");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  // Checkout
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({ address: "", apt: "", city: "", zip: "", notes: "", scheduled_time: "", discount_code: "", payment_method: "pay_at_pickup" });
  const [checkoutDiscount, setCheckoutDiscount] = useState(null); // applied special from code
  const [orderConfirmation, setOrderConfirmation] = useState(null);

  // Customer auth
  const [customer, setCustomer] = useState(null);
  const [customerToken, setCustomerToken] = useState(() => localStorage.getItem("weborder_token") || "");
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // login, register, forgot, reset
  const [authForm, setAuthForm] = useState({ first_name: "", last_name: "", email: "", password: "", phone: "" });
  const [authLoading, setAuthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetToken, setResetToken] = useState("");

  // Item config
  const [configProduct, setConfigProduct] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedMods, setSelectedMods] = useState({});
  const [configStep, setConfigStep] = useState(0); // 0=size, 1..n=modifier groups
  const [itemQty, setItemQty] = useState(1);
  const [includedExtras, setIncludedExtras] = useState({}); // { modifierId: { productId: extraQty } }

  // My Orders
  const [activeView, setActiveView] = useState("menu"); // menu | my-orders
  const [myOrders, setMyOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  // Banner carousel
  const [bannerIdx, setBannerIdx] = useState(0);

  // Loyalty
  const [loyaltyData, setLoyaltyData] = useState(null); // { loyalty_points, loyalty_enabled, loyalty_min_points_redeem, loyalty_redeem_discount }
  const [loyaltyRedeemed, setLoyaltyRedeemed] = useState(false);

  const fetchMenu = useCallback(async (acctId) => {
    try {
      const params = acctId ? `?account_id=${acctId}` : "";
      const res = await api.get(`/weborder/menu${params}`);
      setMenuData(res.data);
    } catch (err) {
      if (err.response?.status === 503) {
        setMenuData({ disabled: true });
      }
    } finally { setLoading(false); }
  }, []);

  // Resolve slug to account_id on mount
  useEffect(() => {
    if (slug) {
      api.get(`/weborder/resolve-slug/${slug}`)
        .then((res) => {
          setAccountId(res.data.account_id);
          fetchMenu(res.data.account_id);
        })
        .catch((err) => {
          if (err.response?.status === 404) {
            setSlugError("Store not found. Please check the URL.");
          } else if (err.response?.status === 503) {
            setMenuData({ disabled: true });
          } else {
            setSlugError("Unable to load store. Please try again.");
          }
          setLoading(false);
        });
    } else {
      fetchMenu("");
    }
  }, [slug, fetchMenu]);

  // Auto-login if token exists
  useEffect(() => {
    if (customerToken) {
      api.get("/customer-auth/me", { headers: { Authorization: `Bearer ${customerToken}` } })
        .then((res) => {
          setCustomer(res.data);
          // Fetch loyalty balance
          const params = accountId ? `?account_id=${accountId}` : "";
          api.get(`/weborder/loyalty${params}`, { headers: { Authorization: `Bearer ${customerToken}` } })
            .then((lr) => setLoyaltyData(lr.data))
            .catch(() => {});
        })
        .catch(() => { localStorage.removeItem("weborder_token"); setCustomerToken(""); });
    }
  }, [customerToken, accountId]);

  // Auto-rotate banners
  useEffect(() => {
    const bannerList = menuData?.settings?.weborder_banner_images || [];
    if (bannerList.length <= 1) return;
    const timer = setInterval(() => setBannerIdx((i) => (i + 1) % bannerList.length), 5000);
    return () => clearInterval(timer);
  }, [menuData]);

  const handleAuth = async (mode) => {
    setAuthLoading(true);
    try {
      if (mode === "login") {
        const res = await api.post("/customer-auth/login", { email: authForm.email, password: authForm.password });
        setCustomer(res.data.customer);
        setCustomerToken(res.data.token);
        localStorage.setItem("weborder_token", res.data.token);
        toast.success(`Welcome back, ${res.data.customer.first_name || ""}!`);
        setShowAuth(false);
      } else if (mode === "register") {
        if (!authForm.first_name || !authForm.email || !authForm.password) { toast.error("Fill in all required fields"); setAuthLoading(false); return; }
        const res = await api.post("/customer-auth/register", authForm);
        setCustomer(res.data.customer);
        setCustomerToken(res.data.token);
        localStorage.setItem("weborder_token", res.data.token);
        toast.success("Account created!");
        setShowAuth(false);
      } else if (mode === "forgot") {
        const res = await api.post("/customer-auth/request-reset", { email: authForm.email });
        toast.success(res.data.message);
        if (res.data._debug_token) setResetToken(res.data._debug_token); // Debug only
        setAuthMode("reset");
      } else if (mode === "reset") {
        await api.post("/customer-auth/reset-password", { token: resetToken, password: authForm.password });
        toast.success("Password reset! You can now sign in.");
        setAuthMode("login");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setAuthLoading(false); }
  };

  const handleLogout = () => {
    setCustomer(null);
    setCustomerToken("");
    localStorage.removeItem("weborder_token");
    setActiveView("menu");
    toast.success("Signed out");
  };

  const fetchMyOrders = useCallback(async () => {
    if (!customerToken) return;
    setOrdersLoading(true);
    try {
      const params = accountId ? `?account_id=${accountId}` : "";
      const res = await api.get(`/weborder/my-orders${params}`, { headers: { Authorization: `Bearer ${customerToken}` } });
      setMyOrders(res.data);
    } catch { toast.error("Failed to load orders"); }
    finally { setOrdersLoading(false); }
  }, [customerToken]);

  const openMyOrders = () => {
    if (!customer) { openAuth("login"); return; }
    setActiveView("my-orders");
    fetchMyOrders();
  };

  const openAuth = (mode = "login") => {
    setAuthMode(mode);
    setAuthForm({ first_name: "", last_name: "", email: "", password: "", phone: "" });
    setShowPassword(false);
    setResetToken("");
    setShowAuth(true);
  };

  // Checkout functions
  const openCheckout = () => {
    if (cart.length === 0) { toast.error("Cart is empty"); return; }
    setCheckoutForm({
      address: "", apt: "", city: "", zip: "", notes: "", scheduled_time: "",
      discount_code: "", payment_method: orderType === "pickup" ? "pay_at_pickup" : "pay_at_delivery",
      guest_name: customer ? "" : "", guest_email: customer ? "" : "", guest_phone: customer ? "" : "",
    });
    setCheckoutDiscount(null);
    setLoyaltyRedeemed(false);
    setOrderConfirmation(null);
    setShowCart(false);
    setShowCheckout(true);
  };

  const applyCheckoutCode = async () => {
    if (!checkoutForm.discount_code.trim()) return;
    try {
      const res = await api.post("/weborder/validate-discount", { code: checkoutForm.discount_code.trim(), account_id: accountId });
      setCheckoutDiscount(res.data);
      toast.success(`Code applied: ${res.data.display_name || res.data.name}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid code"); }
  };

  const calcCheckoutDiscount = () => {
    if (!checkoutDiscount) return 0;
    if (checkoutDiscount.discount_type === "percentage") return cartSubtotal * (checkoutDiscount.discount_amount / 100);
    return Math.min(checkoutDiscount.discount_amount, cartSubtotal);
  };

  // Checkout computed values are defined after cartSubtotal below

  const submitOrder = async () => {
    if (orderType === "delivery" && !checkoutForm.address.trim()) { toast.error("Delivery address required"); return; }
    if (!customer && !checkoutForm.guest_name.trim()) { toast.error("Please enter your name"); return; }
    setCheckoutLoading(true);
    try {
      const deliveryAddr = orderType === "delivery" ? [checkoutForm.address, checkoutForm.apt, checkoutForm.city, checkoutForm.zip].filter(Boolean).join(", ") : "";
      const payload = {
        items: cart,
        order_type: orderType,
        account_id: accountId,
        subtotal: Math.round(cartSubtotal * 100) / 100,
        discount: Math.round(realCheckoutDiscountAmt * 100) / 100,
        tax: Math.round(realCheckoutTax * 100) / 100,
        total: Math.round(realCheckoutTotal * 100) / 100,
        payment_method: checkoutForm.payment_method,
        delivery_address: deliveryAddr,
        notes: checkoutForm.notes,
        scheduled_time: checkoutForm.scheduled_time,
        guest_name: checkoutForm.guest_name || "",
        guest_email: checkoutForm.guest_email || "",
        guest_phone: checkoutForm.guest_phone || "",
        applied_specials: checkoutDiscount ? [{ special_id: checkoutDiscount.special_id, name: checkoutDiscount.name, amount: checkoutDiscount.discount_amount, type: checkoutDiscount.discount_type }] : [],
      };
      const headers = customerToken ? { Authorization: `Bearer ${customerToken}` } : {};
      const res = await api.post("/weborder/submit", payload, { headers });
      setOrderConfirmation(res.data);
      setCart([]);
      setCheckoutDiscount(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Order failed"); }
    finally { setCheckoutLoading(false); }
  };

  if (slugError) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-slate-800 mb-2" data-testid="slug-error-title">Store Not Found</h1>
        <p className="text-slate-500" data-testid="slug-error-message">{slugError}</p>
      </div>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-600 border-t-transparent" />
    </div>
  );

  if (!menuData || menuData.disabled) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Online Ordering Unavailable</h1>
        <p className="text-slate-500">Please check back later or call us to place an order.</p>
      </div>
    </div>
  );

  const { settings, categories, products, specials } = menuData;
  const sym = settings.currency_symbol || "$";
  const brandName = settings.brand_name || "CashUpPOS";
  const allBanners = settings.weborder_banner_images || [];
  // Filter banners by current day — empty active_days means show every day
  const today = new Date().getDay();
  const banners = allBanners.filter((b) => !b.active_days || b.active_days.length === 0 || b.active_days.includes(today));
  const featuredIds = settings.weborder_featured_product_ids || [];
  const featuredProducts = featuredIds.length > 0 ? products.filter((p) => featuredIds.includes(p.product_id)) : products.filter((p) => p.image_url).slice(0, 6);

  const filteredProducts = products.filter((p) => {
    if (selectedCat !== "all" && p.category_id !== selectedCat) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const getCatName = (id) => categories.find((c) => c.category_id === id)?.name || "";

  // Group products by category for display
  const groupedProducts = {};
  filteredProducts.forEach((p) => {
    const catName = getCatName(p.category_id) || "Other";
    if (!groupedProducts[catName]) groupedProducts[catName] = [];
    groupedProducts[catName].push(p);
  });

  // Cart functions
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const loyaltyDiscountAmt = loyaltyRedeemed && loyaltyData ? loyaltyData.loyalty_redeem_discount : 0;
  const realCheckoutDiscountAmt = calcCheckoutDiscount() + loyaltyDiscountAmt;
  const realCheckoutTax = (cartSubtotal - realCheckoutDiscountAmt) * ((menuData?.settings?.tax_rate || 0) / 100);
  const realCheckoutTotal = cartSubtotal - realCheckoutDiscountAmt + realCheckoutTax;

  const openItemConfig = async (product) => {
    setConfigLoading(true);
    setConfigProduct(product);
    setSelectedSize(null);
    setSelectedMods({});
    setIncludedExtras({});
    setConfigStep(0);
    setItemQty(1);
    try {
      const params = accountId ? `?account_id=${accountId}` : "";
      const res = await api.get(`/weborder/product/${product.product_id}/config${params}`);
      setConfigData(res.data);
      // Pre-select product defaults
      const defaults = {};
      for (const g of (res.data.resolved_modifier_groups || [])) {
        const defMods = (g.modifiers || []).filter(m => m.is_product_default);
        if (defMods.length > 0) defaults[g.modifier_group_id] = defMods;
      }
      setSelectedMods(defaults);
      if (!res.data.resolved_size_group?.sizes?.length) {
        setConfigStep(1); // skip size
      }
    } catch { toast.error("Failed to load item details"); setConfigProduct(null); }
    finally { setConfigLoading(false); }
  };

  const addConfigToCart = () => {
    if (!configData) return;
    let basePrice = configData.price || 0;
    if (selectedSize) {
      const spm = configData.size_prices_map || {};
      if (spm[selectedSize.size_id] !== undefined) basePrice = parseFloat(spm[selectedSize.size_id]);
    }
    let modTotal = 0;
    const modParts = [];
    const groups = configData.resolved_modifier_groups || [];
    for (const g of groups) {
      const gid = g.modifier_group_id;
      const sel = selectedMods[gid] || [];
      const freeAmt = g.free_amount || 0;
      let freeUsed = 0;
      sel.forEach((m) => {
        const price = getModPrice(m);
        const modFreeQty = m.free_quantity != null ? m.free_quantity : 0;
        const qty = m.qty || 1;
        for (let q = 0; q < qty; q++) {
          if (modFreeQty > 0 && q < modFreeQty) continue;
          if (freeUsed < freeAmt) { freeUsed++; continue; }
          modTotal += price;
        }
        modParts.push(qty > 1 ? `${qty}x ${m.name}` : m.name);
        if (m.included_items?.length) {
          const inclItems = getIncludedQty(m, selectedSize?.size_id);
          inclItems.forEach((inc) => {
            const extraQty = includedExtras[m.modifier_id]?.[inc.product_id] || 0;
            if (extraQty > 0) modParts.push(`${inc.included_qty + extraQty}x ${inc.name} (+${extraQty} extra)`);
          });
        }
      });
    }
    const extrasTotal = calcIncludedExtrasTotal();
    const totalPrice = Math.round((basePrice + modTotal + extrasTotal) * 100) / 100;
    const parts = [];
    if (selectedSize) parts.push(selectedSize.name);
    parts.push(...modParts);
    setCart((prev) => [...prev, {
      product_id: configData.product_id,
      name: configData.name,
      price: totalPrice,
      quantity: itemQty,
      config_id: `web_${Date.now()}`,
      config_desc: parts.join(", "),
      size_name: selectedSize?.name || "",
      image_url: configData.image_url || "",
    }]);
    setConfigProduct(null);
    setConfigData(null);
    setIncludedExtras({});
    toast.success(`Added ${configData.name} to cart`);
  };

  const addSimpleToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product_id === product.product_id && !i.config_id);
      if (existing) return prev.map((i) => i === existing ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { product_id: product.product_id, name: product.name, price: product.price, quantity: 1, image_url: product.image_url || "" }];
    });
    toast.success(`Added ${product.name}`);
  };

  const handleProductClick = (product) => {
    const hasSizes = !!product.size_group_id;
    const hasMods = product.modifier_group_ids?.length > 0;
    if (hasSizes || hasMods) {
      openItemConfig(product);
    } else {
      addSimpleToCart(product);
    }
  };

  const removeCartItem = (idx) => setCart((prev) => prev.filter((_, i) => i !== idx));
  const updateCartQty = (idx, delta) => {
    setCart((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      const newQty = item.quantity + delta;
      return newQty > 0 ? { ...item, quantity: newQty } : item;
    }).filter((item) => item.quantity > 0));
  };

  // Config dialog helpers
  const configSizes = configData?.resolved_size_group?.sizes || [];
  const configGroups = configData?.resolved_modifier_groups || [];
  const totalSteps = (configSizes.length > 0 ? 1 : 0) + configGroups.length;
  const currentGroup = configStep > 0 ? configGroups[configStep - (configSizes.length > 0 ? 1 : 0)] : null;

  const toggleMod = (group, mod) => {
    const gid = group.modifier_group_id;
    const current = selectedMods[gid] || [];
    const exists = current.find((m) => m.modifier_id === mod.modifier_id);
    if (exists) {
      setSelectedMods((prev) => ({ ...prev, [gid]: current.filter((m) => m.modifier_id !== mod.modifier_id) }));
    } else if (current.length < (group.max_select || 99)) {
      setSelectedMods((prev) => ({ ...prev, [gid]: [...current, mod] }));
    }
  };

  const canProceed = () => {
    if (configStep === 0 && configSizes.length > 0) return !!selectedSize;
    if (currentGroup) {
      const sel = (selectedMods[currentGroup.modifier_group_id] || []).length;
      return sel >= (currentGroup.min_select || 0);
    }
    return true;
  };

  const nextStep = () => {
    if (configStep < totalSteps - 1) setConfigStep(configStep + 1);
    else addConfigToCart();
  };

  const getModPrice = (mod) => {
    if (selectedSize) {
      const offsets = mod.size_offsets;
      if (offsets) {
        if (Array.isArray(offsets)) {
          const match = offsets.find((so) => so.size_id === selectedSize.size_id);
          if (match) return parseFloat(match.price) || 0;
        } else if (typeof offsets === "object") {
          const szPrice = offsets[selectedSize.size_id];
          if (szPrice !== undefined && szPrice !== null) return parseFloat(szPrice) || 0;
        }
      }
    }
    return mod.price || 0;
  };

  // Included items helpers
  const getIncludedQty = (mod, sizeId) => {
    if (!mod.included_items?.length) return [];
    return mod.included_items.map((item) => {
      let qty = item.default_qty || 1;
      if (sizeId && item.size_quantities?.[sizeId] !== undefined) {
        qty = item.size_quantities[sizeId];
      }
      return { ...item, included_qty: qty };
    }).filter((item) => item.included_qty > 0);
  };

  const getIncludedExtraPrice = (item) => item.price_each != null ? item.price_each : 0;

  const calcIncludedExtrasTotal = () => {
    let total = 0;
    for (const [modId, extras] of Object.entries(includedExtras)) {
      let mod = null;
      for (const g of (configData?.resolved_modifier_groups || [])) {
        mod = (g.modifiers || []).find((m) => m.modifier_id === modId);
        if (mod) break;
      }
      if (!mod) continue;
      for (const [prodId, extraQty] of Object.entries(extras)) {
        if (extraQty > 0) {
          const item = (mod.included_items || []).find((i) => i.product_id === prodId);
          if (item) total += getIncludedExtraPrice(item) * extraQty;
        }
      }
    }
    return Math.round(total * 100) / 100;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="weborder-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {settings.logo_url && <img src={settings.logo_url} alt="" className="h-10 w-10 rounded-lg object-cover" />}
            <h1 className="text-xl font-bold text-slate-900" data-testid="weborder-brand">{brandName}</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1.5 text-sm text-slate-500">
              {settings.business_phone && <><Phone className="h-3.5 w-3.5" /> {settings.business_phone}</>}
            </div>
            {customer ? (
              <div className="flex items-center gap-2">
                <button onClick={openMyOrders} className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors" data-testid="weborder-my-orders-btn">
                  <ClipboardList className="h-4 w-4" /> My Orders
                </button>
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-slate-800">{customer.first_name} {customer.last_name}</p>
                  {loyaltyData?.loyalty_enabled ? (
                    <p className="text-[10px] text-blue-600 font-semibold">{loyaltyData.loyalty_points} loyalty pts</p>
                  ) : (
                    <p className="text-[10px] text-slate-400">{customer.email}</p>
                  )}
                </div>
                <button onClick={handleLogout} className="p-2 rounded-full hover:bg-slate-100 text-slate-500" data-testid="weborder-logout"><LogOut className="h-4 w-4" /></button>
              </div>
            ) : (
              <button onClick={() => openAuth("login")} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-slate-700 border border-slate-200 hover:bg-slate-50" data-testid="weborder-signin-btn">
                <User className="h-4 w-4" /> Sign In
              </button>
            )}
            <button onClick={() => setShowCart(true)} className="relative flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-full hover:bg-slate-800 transition-colors" data-testid="weborder-cart-btn">
              <ShoppingCart className="h-4 w-4" />
              <span className="text-sm font-medium">{sym}{cartSubtotal.toFixed(2)}</span>
              {cartCount > 0 && <Badge className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full p-0 flex items-center justify-center text-[10px] bg-red-500 text-white border-2 border-white">{cartCount}</Badge>}
            </button>
          </div>
        </div>
      </header>

      {/* === MY ORDERS VIEW === */}
      {activeView === "my-orders" && (
        <div className="max-w-3xl mx-auto px-4 py-6" data-testid="my-orders-view">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button onClick={() => setActiveView("menu")} className="p-2 rounded-full hover:bg-slate-200 text-slate-500" data-testid="back-to-menu-btn">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div>
                <h2 className="text-xl font-bold text-slate-900">My Orders</h2>
                <p className="text-sm text-slate-500">{myOrders.length} order{myOrders.length !== 1 ? "s" : ""}</p>
              </div>
            </div>
            <button onClick={fetchMyOrders} disabled={ordersLoading} className="text-sm text-blue-600 hover:underline font-medium" data-testid="refresh-orders-btn">
              {ordersLoading ? "Loading..." : "Refresh"}
            </button>
          </div>

          {ordersLoading && myOrders.length === 0 ? (
            <div className="text-center py-16 text-slate-400">Loading your orders...</div>
          ) : myOrders.length === 0 ? (
            <div className="text-center py-16" data-testid="no-orders-message">
              <ClipboardList className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-1">No orders yet</p>
              <p className="text-sm text-slate-400 mb-4">Place your first order to see it here</p>
              <button onClick={() => setActiveView("menu")} className="text-blue-600 text-sm font-medium hover:underline">Browse Menu</button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="orders-list">
              {myOrders.map((order) => {
                const isExpanded = expandedOrderId === order.order_id;
                const orderDate = order.created_at ? new Date(order.created_at) : null;
                const statusColors = { pending: "bg-amber-100 text-amber-700", confirmed: "bg-blue-100 text-blue-700", preparing: "bg-purple-100 text-purple-700", ready: "bg-green-100 text-green-700", completed: "bg-emerald-100 text-emerald-700", cancelled: "bg-red-100 text-red-700" };
                return (
                  <div key={order.order_id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`order-card-${order.order_id}`}>
                    {/* Order Summary Row */}
                    <button onClick={() => setExpandedOrderId(isExpanded ? null : order.order_id)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors text-left" data-testid={`order-toggle-${order.order_id}`}>
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          <Receipt className="h-5 w-5 text-slate-500" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-900">{order.ticket_number || `#${order.order_id.slice(-6)}`}</span>
                            <Badge className={`text-[10px] ${statusColors[order.status] || "bg-slate-100 text-slate-600"}`}>{order.status}</Badge>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-xs text-slate-400">
                              {orderDate ? orderDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                              {orderDate ? ` at ${orderDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : ""}
                            </span>
                            {order.order_type && <span className="text-xs text-slate-400 capitalize">{order.order_type}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-slate-900">{sym}{(order.total || 0).toFixed(2)}</span>
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                      </div>
                    </button>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/30" data-testid={`order-detail-${order.order_id}`}>
                        {/* Items */}
                        <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Items</h4>
                        <div className="space-y-2 mb-4">
                          {(order.items || []).map((item, idx) => (
                            <div key={idx} className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-slate-500 font-medium w-5">{item.quantity || 1}x</span>
                                  <span className="text-sm font-medium text-slate-800">{item.name}</span>
                                </div>
                                {item.config_description && <p className="text-xs text-slate-400 ml-7">{item.config_description}</p>}
                              </div>
                              <span className="text-sm text-slate-600 font-medium">{sym}{((item.price || 0) * (item.quantity || 1)).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>

                        {/* Financial Summary */}
                        <div className="border-t border-slate-200 pt-3 space-y-1.5">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Subtotal</span>
                            <span className="text-slate-700">{sym}{(order.subtotal || 0).toFixed(2)}</span>
                          </div>
                          {(order.discount || 0) > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-green-600">Discount</span>
                              <span className="text-green-600">-{sym}{order.discount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Tax</span>
                            <span className="text-slate-700">{sym}{(order.tax || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2">
                            <span className="text-slate-900">Total</span>
                            <span className="text-slate-900">{sym}{(order.total || 0).toFixed(2)}</span>
                          </div>
                        </div>

                        {/* Payment & Delivery */}
                        <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t border-slate-200">
                          {order.payment_method && (
                            <div>
                              <span className="text-[10px] text-slate-400 uppercase tracking-wider">Payment</span>
                              <p className="text-xs font-medium text-slate-700 capitalize">{order.payment_method.replace(/_/g, " ")}</p>
                            </div>
                          )}
                          {order.order_type && (
                            <div>
                              <span className="text-[10px] text-slate-400 uppercase tracking-wider">Type</span>
                              <p className="text-xs font-medium text-slate-700 capitalize">{order.order_type}</p>
                            </div>
                          )}
                          {order.scheduled_time && (
                            <div>
                              <span className="text-[10px] text-slate-400 uppercase tracking-wider">Scheduled</span>
                              <p className="text-xs font-medium text-slate-700">{new Date(order.scheduled_time).toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                        {order.delivery_address && (
                          <div className="mt-2 flex items-start gap-1.5">
                            <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                            <span className="text-xs text-slate-500">{order.delivery_address}</span>
                          </div>
                        )}
                        {order.notes && (
                          <p className="text-xs text-slate-400 mt-2 italic">Note: {order.notes}</p>
                        )}

                        {/* Reorder Button */}
                        <div className="mt-4 pt-3 border-t border-slate-200">
                          <button onClick={() => { setActiveView("menu"); toast.info("Browse the menu to place a new order"); }}
                            className="text-sm text-blue-600 hover:underline font-medium" data-testid={`reorder-btn-${order.order_id}`}>
                            Order Again
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* === MENU VIEW === */}
      {activeView === "menu" && <>
      {/* Order Type Toggle */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <span className="text-sm text-slate-500">Order for:</span>
          <div className="flex bg-slate-100 rounded-full p-1" data-testid="weborder-type-toggle">
            {settings.weborder_pickup_enabled !== false && (
              <button onClick={() => setOrderType("pickup")} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${orderType === "pickup" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`} data-testid="weborder-pickup-btn">
                <Store className="h-4 w-4" /> Pickup
              </button>
            )}
            {settings.weborder_delivery_enabled !== false && (
              <button onClick={() => setOrderType("delivery")} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${orderType === "delivery" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`} data-testid="weborder-delivery-btn">
                <Truck className="h-4 w-4" /> Delivery
              </button>
            )}
          </div>
          {settings.business_address && (
            <div className="hidden md:flex items-center gap-1.5 text-sm text-slate-400 ml-auto">
              <MapPin className="h-3.5 w-3.5" /> {settings.business_address}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Hero Banner */}
        {banners.length > 0 && (
          <div className="mb-8 rounded-2xl overflow-hidden" data-testid="weborder-hero-banner">
            <div className="relative">
              <img src={banners[bannerIdx % banners.length].url} alt={banners[bannerIdx % banners.length].title || ""} className="w-full h-[200px] sm:h-[280px] lg:h-[340px] object-cover transition-opacity duration-500" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent flex items-end p-6 sm:p-8">
                <div>
                  {banners[bannerIdx % banners.length].title && <h2 className="text-2xl sm:text-4xl font-black text-white mb-1 drop-shadow-lg">{banners[bannerIdx % banners.length].title}</h2>}
                  {banners[bannerIdx % banners.length].subtitle && <p className="text-sm sm:text-lg text-white/90 drop-shadow">{banners[bannerIdx % banners.length].subtitle}</p>}
                </div>
              </div>
              {banners.length > 1 && (
                <div className="absolute bottom-3 right-4 flex gap-1.5">
                  {banners.map((_, i) => (
                    <button key={i} onClick={() => setBannerIdx(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${i === bannerIdx % banners.length ? "bg-white scale-110" : "bg-white/50 hover:bg-white/70"}`} data-testid={`banner-dot-${i}`} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Active Specials */}
        {specials.length > 0 && (
          <div className="mb-8" data-testid="weborder-specials">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /> Today's Specials</h2>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {specials.map((s) => (
                <div key={s.special_id} className="flex-shrink-0 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 min-w-[200px] max-w-[250px]" data-testid={`weborder-special-${s.special_id}`}>
                  <p className="text-lg font-black text-amber-800">{s.discount_type === "percentage" ? `${s.discount_amount}% OFF` : `${sym}${(s.discount_amount || 0).toFixed(2)} OFF`}</p>
                  <p className="text-sm font-semibold text-slate-800 mt-1">{s.display_name || s.name}</p>
                  {s.discount_code && <Badge className="mt-2 text-xs font-mono bg-amber-200 text-amber-800 border-amber-300">{s.discount_code}</Badge>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Featured Items */}
        {featuredProducts.length > 0 && selectedCat === "all" && !search && (
          <div className="mb-8" data-testid="weborder-featured">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Featured</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {featuredProducts.slice(0, 4).map((p) => (
                <button key={p.product_id} onClick={() => handleProductClick(p)} className="bg-white rounded-xl overflow-hidden border border-slate-200 hover:shadow-lg hover:border-slate-300 transition-all text-left group" data-testid={`weborder-featured-${p.product_id}`}>
                  {p.image_url && <div className="aspect-[4/3] overflow-hidden"><img src={p.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" /></div>}
                  <div className="p-3">
                    <p className="text-sm font-semibold text-slate-800 line-clamp-1">{p.name}</p>
                    <p className="text-base font-bold text-slate-900 mt-0.5">{sym}{(p.price || 0).toFixed(2)}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Category Nav + Search */}
        <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="weborder-categories">
          <div className="flex gap-2 overflow-x-auto flex-1">
            <button onClick={() => setSelectedCat("all")} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${selectedCat === "all" ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>All</button>
            {categories.map((c) => (
              <button key={c.category_id} onClick={() => setSelectedCat(c.category_id)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${selectedCat === c.category_id ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`} data-testid={`weborder-cat-${c.category_id}`}>
                {c.name}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search menu..." className="pl-9 w-48 rounded-full" data-testid="weborder-search" />
          </div>
        </div>

        {/* Product Grid */}
        {Object.entries(groupedProducts).map(([catName, prods]) => (
          <div key={catName} className="mb-8" data-testid={`weborder-cat-section-${catName}`}>
            <h3 className="text-lg font-bold text-slate-800 mb-3">{catName}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {prods.map((p) => (
                <button key={p.product_id} onClick={() => handleProductClick(p)} className="bg-white rounded-xl border border-slate-200 hover:shadow-md hover:border-slate-300 transition-all flex overflow-hidden text-left group" data-testid={`weborder-product-${p.product_id}`}>
                  {p.image_url && (
                    <div className="w-28 h-28 flex-shrink-0 overflow-hidden">
                      <img src={p.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    </div>
                  )}
                  <div className="p-3 flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-800 line-clamp-1">{p.name}</p>
                      {p.description && <p className="text-xs text-slate-400 line-clamp-2 mt-0.5">{p.description}</p>}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-base font-bold text-slate-900">{sym}{(p.price || 0).toFixed(2)}</span>
                      <div className="h-7 w-7 rounded-full bg-slate-900 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Plus className="h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}

        {filteredProducts.length === 0 && (
          <div className="text-center py-16 text-slate-400">
            <p className="text-lg">No items found</p>
            <p className="text-sm mt-1">Try a different category or search term</p>
          </div>
        )}
      </div>

      {/* Item Configuration Dialog */}
      <Dialog open={!!configProduct} onOpenChange={(open) => { if (!open) { setConfigProduct(null); setConfigData(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col p-0" data-testid="weborder-config-dialog">
          {configLoading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-900 border-t-transparent" /></div>
          ) : configData && (
            <>
              {configData.image_url && (
                <div className="w-full h-48 overflow-hidden flex-shrink-0">
                  <img src={configData.image_url} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="px-6 pt-4 pb-2">
                <h2 className="text-xl font-bold text-slate-900">{configData.name}</h2>
                {configData.description && <p className="text-sm text-slate-500 mt-1">{configData.description}</p>}
              </div>

              <ScrollArea className="flex-1 px-6">
                {/* Size selection */}
                {configStep === 0 && configSizes.length > 0 && (
                  <div className="space-y-2 pb-4" data-testid="weborder-size-step">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Choose Size</p>
                    {configSizes.map((size) => {
                      const sizePrice = configData.size_prices_map?.[size.size_id];
                      const isSelected = selectedSize?.size_id === size.size_id;
                      return (
                        <button key={size.size_id} onClick={() => setSelectedSize(size)} className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${isSelected ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`weborder-size-${size.size_id}`}>
                          <div className="flex items-center gap-3">
                            <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${isSelected ? "border-slate-900" : "border-slate-300"}`}>
                              {isSelected && <div className="h-3 w-3 rounded-full bg-slate-900" />}
                            </div>
                            <div>
                              <span className="text-sm font-medium text-slate-800">{size.name}</span>
                              {(configData.size_included_items?.[size.size_id] || []).map((inc, i) => (
                                <p key={i} className="text-[10px] text-emerald-600">Includes: {inc.quantity > 1 ? `${inc.quantity}x ` : ""}{inc.name}</p>
                              ))}
                            </div>
                          </div>
                          {sizePrice !== undefined && <span className="text-sm font-bold text-slate-900">{sym}{parseFloat(sizePrice).toFixed(2)}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Modifier groups */}
                {currentGroup && (
                  <div className="space-y-2 pb-4" data-testid="weborder-mod-step">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{currentGroup.name}</p>
                      <span className="text-xs text-slate-400">
                        {currentGroup.min_select > 0 ? `Select ${currentGroup.min_select}` : "Optional"}{currentGroup.max_select < 99 ? ` (max ${currentGroup.max_select})` : ""}
                        {currentGroup.free_amount > 0 ? ` · ${currentGroup.free_amount} free` : ""}
                      </span>
                    </div>
                    {(currentGroup.modifiers || []).map((mod) => {
                      const isSelected = (selectedMods[currentGroup.modifier_group_id] || []).some((m) => m.modifier_id === mod.modifier_id);
                      const modPrice = getModPrice(mod);
                      const selCount = (selectedMods[currentGroup.modifier_group_id] || []).length;
                      const selIdx = isSelected ? (selectedMods[currentGroup.modifier_group_id] || []).findIndex((m) => m.modifier_id === mod.modifier_id) : -1;
                      const modFreeQty = mod.free_quantity != null ? mod.free_quantity : 0;
                      const isFreeByMod = modFreeQty > 0;
                      const isFreeByGroup = isSelected && selIdx < (currentGroup.free_amount || 0);
                      const isFree = isSelected && (isFreeByMod || isFreeByGroup);
                      const inclItems = isSelected && mod.included_items?.length ? getIncludedQty(mod, selectedSize?.size_id) : [];
                      return (
                        <div key={mod.modifier_id} className="space-y-0">
                          <button onClick={() => toggleMod(currentGroup, mod)} disabled={!isSelected && selCount >= (currentGroup.max_select || 99)} className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${isSelected ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"} ${inclItems.length > 0 ? "rounded-b-none" : ""}`} data-testid={`weborder-mod-${mod.modifier_id}`}>
                            <div className="flex items-center gap-3">
                              <div className={`h-5 w-5 rounded border-2 flex items-center justify-center ${isSelected ? "bg-slate-900 border-slate-900" : "border-slate-300"}`}>
                                {isSelected && <Check className="h-3 w-3 text-white" />}
                              </div>
                              {mod.image_url && <img src={mod.image_url} alt="" className="h-8 w-8 rounded object-cover" loading="lazy" />}
                              <span className="text-sm font-medium text-slate-800">{mod.name}</span>
                              {mod.is_product_default && <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200">Default</Badge>}
                            </div>
                            <div className="flex items-center gap-1.5">
                              {modPrice > 0 && !isFree && <span className="text-sm text-slate-600">+{sym}{modPrice.toFixed(2)}</span>}
                              {modPrice > 0 && isFree && <span className="text-sm text-slate-400 line-through">+{sym}{modPrice.toFixed(2)}</span>}
                              {isFree && <Badge className="text-[10px] bg-emerald-100 text-emerald-700">FREE</Badge>}
                            </div>
                          </button>
                          {/* Included Items / Comes With */}
                          {inclItems.length > 0 && (
                            <div className="bg-amber-50 border-2 border-t-0 border-slate-900 rounded-b-xl p-3 space-y-2" data-testid={`weborder-included-${mod.modifier_id}`}>
                              <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider flex items-center gap-1">
                                <Package className="h-3 w-3" /> Comes With
                              </p>
                              {inclItems.map((inc) => {
                                const extraQty = includedExtras[mod.modifier_id]?.[inc.product_id] || 0;
                                const extraPrice = getIncludedExtraPrice(inc);
                                const maxExtra = inc.max_extra ?? 5;
                                return (
                                  <div key={inc.product_id} className="flex items-center justify-between gap-2" data-testid={`weborder-included-item-${mod.modifier_id}-${inc.product_id}`}>
                                    <div className="flex-1 min-w-0">
                                      <span className="text-xs font-medium text-slate-800">{inc.included_qty}x {inc.name}</span>
                                      {extraQty > 0 && <span className="text-[10px] text-amber-600 ml-1">(+{extraQty} extra · {sym}{(extraPrice * extraQty).toFixed(2)})</span>}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      {extraQty > 0 && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setIncludedExtras((prev) => { const me = { ...(prev[mod.modifier_id] || {}) }; me[inc.product_id] = Math.max(0, extraQty - 1); return { ...prev, [mod.modifier_id]: me }; }); }}
                                          className="h-7 w-7 rounded-full bg-white border border-amber-300 flex items-center justify-center hover:bg-amber-100 text-amber-700"
                                          data-testid={`weborder-inc-minus-${mod.modifier_id}-${inc.product_id}`}
                                        ><Minus className="h-3 w-3" /></button>
                                      )}
                                      {extraQty > 0 && <span className="w-5 text-center text-xs font-bold text-amber-700">+{extraQty}</span>}
                                      {extraQty < maxExtra && extraPrice > 0 && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setIncludedExtras((prev) => { const me = { ...(prev[mod.modifier_id] || {}) }; me[inc.product_id] = (me[inc.product_id] || 0) + 1; return { ...prev, [mod.modifier_id]: me }; }); }}
                                          className="h-7 rounded-full bg-white border border-amber-300 flex items-center justify-center hover:bg-amber-100 text-amber-700 px-2 gap-1"
                                          data-testid={`weborder-inc-plus-${mod.modifier_id}-${inc.product_id}`}
                                        >
                                          <Plus className="h-3 w-3" />
                                          <span className="text-[10px] font-medium">{sym}{extraPrice.toFixed(2)}</span>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>

              {/* Qty + Actions */}
              <div className="px-6 pb-4 pt-2 border-t border-slate-100 space-y-3">
                <div className="flex items-center justify-center gap-4">
                  <button onClick={() => setItemQty(Math.max(1, itemQty - 1))} className="h-9 w-9 rounded-full border-2 border-slate-300 flex items-center justify-center hover:bg-slate-50"><Minus className="h-4 w-4" /></button>
                  <span className="text-lg font-bold w-8 text-center">{itemQty}</span>
                  <button onClick={() => setItemQty(itemQty + 1)} className="h-9 w-9 rounded-full border-2 border-slate-300 flex items-center justify-center hover:bg-slate-50"><Plus className="h-4 w-4" /></button>
                </div>
                <div className="flex gap-2">
                  {configStep > 0 && (
                    <Button variant="outline" onClick={() => setConfigStep(configStep - 1)} className="rounded-full">
                      <ChevronLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                  )}
                  <Button onClick={() => { if (canProceed()) nextStep(); else toast.error("Please complete your selection"); }} disabled={!canProceed()} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-full h-11 text-sm font-semibold" data-testid="weborder-config-next">
                    {configStep >= totalSteps - 1 ? (() => {
                      let p = configData.price || 0;
                      if (selectedSize) { const spm = configData.size_prices_map || {}; if (spm[selectedSize.size_id] !== undefined) p = parseFloat(spm[selectedSize.size_id]); }
                      let mt = 0; const groups = configData.resolved_modifier_groups || [];
                      for (const g of groups) {
                        const sel = selectedMods[g.modifier_group_id] || [];
                        const fa = g.free_amount || 0;
                        let freeUsed = 0;
                        sel.forEach((m) => {
                          let mp = m.price || 0;
                          const offsets = m.size_offsets;
                          if (selectedSize && offsets) {
                            if (Array.isArray(offsets)) { const o = offsets.find((so) => so.size_id === selectedSize.size_id); if (o) mp = o.price; }
                            else if (typeof offsets === "object") { const sp = offsets[selectedSize.size_id]; if (sp !== undefined) mp = parseFloat(sp); }
                          }
                          const modFreeQty = m.free_quantity != null ? m.free_quantity : 0;
                          const qty = m.qty || 1;
                          for (let q = 0; q < qty; q++) {
                            if (modFreeQty > 0 && q < modFreeQty) continue;
                            if (freeUsed < fa) { freeUsed++; continue; }
                            mt += mp;
                          }
                        });
                      }
                      const et = calcIncludedExtrasTotal();
                      return `Add to Cart · ${sym}${(Math.round((p + mt + et) * 100) / 100 * itemQty).toFixed(2)}`;
                    })() : "Next"}
                    {configStep < totalSteps - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Cart Drawer */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col" data-testid="weborder-cart-drawer">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" /> Your Order
              <Badge variant="outline" className="text-xs ml-auto">{cartCount} items</Badge>
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 -mx-6 px-6">
            {cart.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <ShoppingCart className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm">Your cart is empty</p>
                <p className="text-xs mt-1">Browse the menu and add items</p>
              </div>
            ) : (
              <div className="space-y-3 py-2">
                {cart.map((item, idx) => (
                  <div key={idx} className="flex gap-3 bg-slate-50 rounded-xl p-3" data-testid={`weborder-cart-item-${idx}`}>
                    {item.image_url && <img src={item.image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 line-clamp-1">{item.name}</p>
                      {item.config_desc && <p className="text-[11px] text-slate-400 line-clamp-1">{item.config_desc}</p>}
                      <p className="text-sm font-bold text-slate-900 mt-1">{sym}{(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                    <div className="flex flex-col items-center justify-between">
                      <button onClick={() => removeCartItem(idx)} className="text-slate-400 hover:text-red-500"><X className="h-4 w-4" /></button>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => updateCartQty(idx, -1)} className="h-6 w-6 rounded-full border border-slate-300 flex items-center justify-center hover:bg-slate-100"><Minus className="h-3 w-3" /></button>
                        <span className="text-sm font-bold w-5 text-center">{item.quantity}</span>
                        <button onClick={() => updateCartQty(idx, 1)} className="h-6 w-6 rounded-full border border-slate-300 flex items-center justify-center hover:bg-slate-100"><Plus className="h-3 w-3" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          {cart.length > 0 && (
            <div className="border-t border-slate-100 pt-3 space-y-2">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-semibold">{sym}{cartSubtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Tax ({settings.tax_rate || 0}%)</span><span className="font-semibold">{sym}{(cartSubtotal * (settings.tax_rate || 0) / 100).toFixed(2)}</span></div>
              <div className="flex justify-between text-base font-bold"><span>Total</span><span>{sym}{(cartSubtotal * (1 + (settings.tax_rate || 0) / 100)).toFixed(2)}</span></div>
              <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full h-12 text-sm font-semibold mt-2" data-testid="weborder-checkout-btn" onClick={openCheckout}>
                Proceed to Checkout · {sym}{(cartSubtotal * (1 + (settings.tax_rate || 0) / 100)).toFixed(2)}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Floating cart button (mobile) */}
      {cartCount > 0 && !showCart && (
        <div className="fixed bottom-4 left-4 right-4 sm:hidden z-30">
          <button onClick={() => setShowCart(true)} className="w-full bg-slate-900 text-white rounded-full h-14 flex items-center justify-between px-6 shadow-xl" data-testid="weborder-floating-cart">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              <Badge className="bg-white text-slate-900 text-xs">{cartCount}</Badge>
            </div>
            <span className="font-semibold">View Cart · {sym}{cartSubtotal.toFixed(2)}</span>
          </button>
        </div>
      )}
      </>}
      {/* Checkout Dialog */}
      <Dialog open={showCheckout && !orderConfirmation} onOpenChange={(open) => { if (!open) setShowCheckout(false); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col" data-testid="weborder-checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Checkout</DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-5 py-2">
              {/* Guest info (if not logged in) */}
              {!customer && (
                <div className="space-y-3">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Your Info</p>
                  <Input value={checkoutForm.guest_name} onChange={(e) => setCheckoutForm({ ...checkoutForm, guest_name: e.target.value })} placeholder="Your Name *" data-testid="checkout-guest-name" />
                  <div className="grid grid-cols-2 gap-3">
                    <Input type="email" value={checkoutForm.guest_email} onChange={(e) => setCheckoutForm({ ...checkoutForm, guest_email: e.target.value })} placeholder="Email" data-testid="checkout-guest-email" />
                    <Input value={checkoutForm.guest_phone} onChange={(e) => setCheckoutForm({ ...checkoutForm, guest_phone: e.target.value })} placeholder="Phone" data-testid="checkout-guest-phone" />
                  </div>
                  <p className="text-xs text-slate-400">
                    <button onClick={() => { setShowCheckout(false); openAuth("login"); }} className="text-blue-600 hover:underline">Sign in</button> or <button onClick={() => { setShowCheckout(false); openAuth("register"); }} className="text-blue-600 hover:underline">create an account</button> for faster checkout next time
                  </p>
                </div>
              )}

              {/* Delivery address */}
              {orderType === "delivery" && (
                <div className="space-y-3">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1"><MapPin className="h-3 w-3" /> Delivery Address</p>
                  <Input value={checkoutForm.address} onChange={(e) => setCheckoutForm({ ...checkoutForm, address: e.target.value })} placeholder="Street Address *" data-testid="checkout-address" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input value={checkoutForm.apt} onChange={(e) => setCheckoutForm({ ...checkoutForm, apt: e.target.value })} placeholder="Apt/Suite" />
                    <Input value={checkoutForm.city} onChange={(e) => setCheckoutForm({ ...checkoutForm, city: e.target.value })} placeholder="City" />
                    <Input value={checkoutForm.zip} onChange={(e) => setCheckoutForm({ ...checkoutForm, zip: e.target.value })} placeholder="ZIP" />
                  </div>
                </div>
              )}

              {/* Scheduled time */}
              <div className="space-y-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Clock className="h-3 w-3" /> When do you want it?</p>
                <div className="flex gap-2">
                  <button onClick={() => setCheckoutForm({ ...checkoutForm, scheduled_time: "" })} className={`flex-1 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${!checkoutForm.scheduled_time ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="checkout-asap">
                    ASAP
                  </button>
                  <button onClick={() => setCheckoutForm({ ...checkoutForm, scheduled_time: "later" })} className={`flex-1 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${checkoutForm.scheduled_time ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="checkout-later">
                    Schedule for Later
                  </button>
                </div>
                {checkoutForm.scheduled_time && (
                  <Input type="datetime-local" value={checkoutForm.scheduled_time === "later" ? "" : checkoutForm.scheduled_time} onChange={(e) => setCheckoutForm({ ...checkoutForm, scheduled_time: e.target.value })} className="mt-1" data-testid="checkout-scheduled-input" />
                )}
              </div>

              {/* Discount code */}
              <div className="space-y-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Discount Code</p>
                <div className="flex gap-2">
                  <Input value={checkoutForm.discount_code} onChange={(e) => setCheckoutForm({ ...checkoutForm, discount_code: e.target.value.toUpperCase() })} placeholder="Enter code" className="font-mono uppercase flex-1" data-testid="checkout-discount-code" onKeyDown={(e) => { if (e.key === "Enter") applyCheckoutCode(); }} />
                  <Button variant="outline" onClick={applyCheckoutCode} data-testid="checkout-apply-code">Apply</Button>
                </div>
                {checkoutDiscount && (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <Check className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-800 flex-1">{checkoutDiscount.display_name || checkoutDiscount.name}</span>
                    <span className="text-sm font-mono text-emerald-700">-{checkoutDiscount.discount_type === "percentage" ? `${checkoutDiscount.discount_amount}%` : `${sym}${checkoutDiscount.discount_amount.toFixed(2)}`}</span>
                    <button onClick={() => setCheckoutDiscount(null)} className="text-emerald-500 hover:text-red-500"><X className="h-4 w-4" /></button>
                  </div>
                )}
              </div>

              {/* Loyalty Redemption */}
              {customer && loyaltyData?.loyalty_enabled && (
                <div className="space-y-2" data-testid="checkout-loyalty-section">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Loyalty Points</p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-semibold text-blue-800">Your Balance: {loyaltyData.loyalty_points} pts</span>
                        {loyaltyData.loyalty_points >= loyaltyData.loyalty_min_points_redeem && (
                          <p className="text-xs text-blue-600">Redeem {loyaltyData.loyalty_min_points_redeem} pts for {sym}{loyaltyData.loyalty_redeem_discount?.toFixed(2)} off</p>
                        )}
                        {loyaltyData.loyalty_points < loyaltyData.loyalty_min_points_redeem && (
                          <p className="text-xs text-blue-500">Need {loyaltyData.loyalty_min_points_redeem - loyaltyData.loyalty_points} more pts to redeem</p>
                        )}
                      </div>
                      {loyaltyData.loyalty_points >= loyaltyData.loyalty_min_points_redeem && !loyaltyRedeemed && (
                        <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100" data-testid="checkout-redeem-loyalty" onClick={async () => {
                          try {
                            const headers = customerToken ? { Authorization: `Bearer ${customerToken}` } : {};
                            const res = await api.post("/loyalty/redeem", { customer_id: customer.customer_id, account_id: accountId }, { headers });
                            setLoyaltyRedeemed(true);
                            setLoyaltyData((prev) => ({ ...prev, loyalty_points: res.data.remaining_points }));
                            toast.success(`Redeemed! ${sym}${res.data.discount_amount.toFixed(2)} off applied`);
                          } catch (err) { toast.error(err.response?.data?.detail || "Redemption failed"); }
                        }}>Redeem</Button>
                      )}
                      {loyaltyRedeemed && (
                        <div className="flex items-center gap-1">
                          <Check className="h-4 w-4 text-blue-600" />
                          <span className="text-xs font-semibold text-blue-700">-{sym}{loyaltyData.loyalty_redeem_discount?.toFixed(2)}</span>
                          <button onClick={() => setLoyaltyRedeemed(false)} className="text-blue-400 hover:text-red-500 ml-1"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Special Instructions</p>
                <textarea value={checkoutForm.notes} onChange={(e) => setCheckoutForm({ ...checkoutForm, notes: e.target.value })} className="flex min-h-[60px] w-full rounded-xl border border-input bg-transparent px-3 py-2 text-sm" placeholder="Any special requests..." data-testid="checkout-notes" />
              </div>

              {/* Order Summary */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Order Summary</p>
                {cart.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-slate-700">{item.quantity}x {item.name}</span>
                    <span className="font-medium">{sym}{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t border-slate-200 pt-2 mt-2 space-y-1">
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span>{sym}{cartSubtotal.toFixed(2)}</span></div>
                  {realCheckoutDiscountAmt > 0 && <div className="flex justify-between text-sm text-emerald-600"><span>Discount</span><span>-{sym}{realCheckoutDiscountAmt.toFixed(2)}</span></div>}
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Tax</span><span>{sym}{realCheckoutTax.toFixed(2)}</span></div>
                  <div className="flex justify-between text-base font-bold pt-1"><span>Total</span><span>{sym}{realCheckoutTotal.toFixed(2)}</span></div>
                </div>
              </div>
            </div>
          </ScrollArea>
          <div className="pt-3 border-t border-slate-100 space-y-2">
            <Button onClick={submitOrder} disabled={checkoutLoading} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full h-12 text-sm font-semibold" data-testid="checkout-submit-btn">
              {checkoutLoading ? "Placing Order..." : `Place ${orderType === "pickup" ? "Pickup" : "Delivery"} Order · ${sym}${realCheckoutTotal.toFixed(2)}`}
            </Button>
            <Button variant="outline" onClick={() => { setShowCheckout(false); setShowCart(true); }} className="w-full rounded-full">Back to Cart</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Confirmation Dialog */}
      <Dialog open={!!orderConfirmation} onOpenChange={(open) => { if (!open) { setOrderConfirmation(null); setShowCheckout(false); } }}>
        <DialogContent className="sm:max-w-md text-center" data-testid="weborder-confirmation-dialog">
          <div className="py-6 space-y-4">
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <Check className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Order Placed!</h2>
            <p className="text-sm text-slate-500">Your order has been received and is being prepared.</p>
            <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-left">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Order #</span>
                <span className="font-bold font-mono text-slate-900">{orderConfirmation?.ticket_number}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Type</span>
                <span className="font-medium capitalize">{orderConfirmation?.order_type}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total</span>
                <span className="font-bold">{sym}{(orderConfirmation?.total || 0).toFixed(2)}</span>
              </div>
              {orderConfirmation?.scheduled_time && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Ready At</span>
                  <span className="font-medium">{new Date(orderConfirmation.scheduled_time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
                </div>
              )}
              {orderConfirmation?.delivery_address && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Delivery To</span>
                  <span className="font-medium text-right max-w-[200px]">{orderConfirmation.delivery_address}</span>
                </div>
              )}
            </div>
            {orderConfirmation?.payment_method?.includes("pay_at") && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Payment due at {orderConfirmation.order_type === "pickup" ? "pickup" : "delivery"}: <strong>{sym}{(orderConfirmation?.total || 0).toFixed(2)}</strong>
              </p>
            )}
            <Button onClick={() => { setOrderConfirmation(null); setShowCheckout(false); }} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full h-11" data-testid="confirmation-done-btn">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md" data-testid="weborder-auth-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-center">
              {authMode === "login" && "Sign In"}
              {authMode === "register" && "Create Account"}
              {authMode === "forgot" && "Reset Password"}
              {authMode === "reset" && "New Password"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {authMode === "register" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">First Name *</Label>
                  <Input value={authForm.first_name} onChange={(e) => setAuthForm({ ...authForm, first_name: e.target.value })} placeholder="First name" data-testid="auth-first-name" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Last Name</Label>
                  <Input value={authForm.last_name} onChange={(e) => setAuthForm({ ...authForm, last_name: e.target.value })} placeholder="Last name" />
                </div>
              </div>
            )}
            {(authMode === "login" || authMode === "register" || authMode === "forgot") && (
              <div>
                <Label className="text-xs text-slate-500">Email *</Label>
                <Input type="email" value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} placeholder="you@example.com" data-testid="auth-email" />
              </div>
            )}
            {(authMode === "login" || authMode === "register") && (
              <div>
                <Label className="text-xs text-slate-500">Password *</Label>
                <div className="relative">
                  <Input type={showPassword ? "text" : "password"} value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} placeholder="Min 6 characters" data-testid="auth-password" onKeyDown={(e) => { if (e.key === "Enter") handleAuth(authMode); }} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}
            {authMode === "register" && (
              <div>
                <Label className="text-xs text-slate-500">Phone</Label>
                <Input value={authForm.phone} onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value })} placeholder="(555) 123-4567" />
              </div>
            )}
            {authMode === "reset" && (
              <>
                <div>
                  <Label className="text-xs text-slate-500">Reset Token</Label>
                  <Input value={resetToken} onChange={(e) => setResetToken(e.target.value)} placeholder="Enter reset token from email" data-testid="auth-reset-token" className="font-mono" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">New Password *</Label>
                  <Input type="password" value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} placeholder="Min 6 characters" />
                </div>
              </>
            )}

            <Button onClick={() => handleAuth(authMode)} disabled={authLoading} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full h-11" data-testid="auth-submit-btn">
              {authLoading ? "..." : authMode === "login" ? "Sign In" : authMode === "register" ? "Create Account" : authMode === "forgot" ? "Send Reset Link" : "Reset Password"}
            </Button>

            {authMode === "login" && (
              <div className="space-y-2 text-center">
                <button onClick={() => setAuthMode("forgot")} className="text-sm text-blue-600 hover:underline" data-testid="auth-forgot-link">Forgot password?</button>
                <p className="text-sm text-slate-500">
                  Don't have an account? <button onClick={() => setAuthMode("register")} className="text-blue-600 hover:underline font-medium" data-testid="auth-register-link">Sign up</button>
                </p>
                <div className="relative my-3"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div><div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-slate-400">or</span></div></div>
                <Button variant="outline" onClick={() => { setShowAuth(false); toast.success("Continuing as guest"); }} className="w-full rounded-full" data-testid="auth-guest-btn">
                  Continue as Guest
                </Button>
              </div>
            )}
            {authMode === "register" && (
              <p className="text-center text-sm text-slate-500">
                Already have an account? <button onClick={() => setAuthMode("login")} className="text-blue-600 hover:underline font-medium">Sign in</button>
              </p>
            )}
            {(authMode === "forgot" || authMode === "reset") && (
              <p className="text-center text-sm text-slate-500">
                <button onClick={() => setAuthMode("login")} className="text-blue-600 hover:underline font-medium">Back to Sign In</button>
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
